import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  getGeneralSettings,
  updateGeneralSettings,
  getCourierFeeSettings,
  updateCourierFeeSettings,
  getKycSettings,
  updateKycSettings,
  getTransactionFeeSettings,
  updateTransactionFeeSettings,
} from "../../apis/settings";
import { toast } from "react-toastify";

interface SettingsState {
  general: any;
  courierFee: any;
  kyc: any;
  transactionFee: any;
  loading: boolean;
  error: string | null;
}

const initialState: SettingsState = {
  general: null,
  courierFee: null,
  kyc: null,
  transactionFee: null,
  loading: false,
  error: null,
};

// Thunks
export const fetchGeneralSettings = createAsyncThunk(
  "settings/fetchGeneralSettings",
  async (_, { rejectWithValue }) => {
    try {
      const res = await getGeneralSettings();
      return res?.data;
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ?? "Failed to fetch general settings"
      );
      return rejectWithValue(
        error.response?.data?.message ?? "Failed to fetch general settings"
      );
    }
  }
);

export const updateGeneralSettingsThunk = createAsyncThunk(
  "settings/updateGeneralSettings",
  async (data: any, { rejectWithValue }) => {
    try {
      const res = await updateGeneralSettings(data);
      return res.data;
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ?? "Failed to update general settings"
      );
      return rejectWithValue(
        error.response?.data?.message ?? "Failed to update general settings"
      );
    }
  }
);

export const fetchCourierFeeSettings = createAsyncThunk(
  "settings/fetchCourierFeeSettings",
  async (courierId: string, { rejectWithValue }) => {
    try {
      const res = await getCourierFeeSettings(courierId);
      return res?.data;
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ?? "Failed to fetch courier fee settings"
      );
      return rejectWithValue(
        error.response?.data?.message ?? "Failed to fetch courier fee settings"
      );
    }
  }
);

export const updateCourierFeeSettingsThunk = createAsyncThunk(
  "settings/updateCourierFeeSettings",
  async (data: any, { rejectWithValue }) => {
    try {
      const res = await updateCourierFeeSettings(data);
      return res.data;
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ?? "Failed to update courier fee settings"
      );
      return rejectWithValue(
        error.response?.data?.message ?? "Failed to update courier fee settings"
      );
    }
  }
);

export const fetchKycSettings = createAsyncThunk(
  "settings/fetchKycSettings",
  async (_, { rejectWithValue }) => {
    try {
      const res = await getKycSettings();
      return res.data;
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ?? "Failed to fetch KYC settings"
      );
      return rejectWithValue(
        error.response?.data?.message ?? "Failed to fetch KYC settings"
      );
    }
  }
);

export const updateKycSettingsThunk = createAsyncThunk(
  "settings/updateKycSettings",
  async (data: any, { rejectWithValue }) => {
    try {
      const res = await updateKycSettings(data);
      return res.data;
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ?? "Failed to update KYC settings"
      );
      return rejectWithValue(
        error.response?.data?.message ?? "Failed to update KYC settings"
      );
    }
  }
);

export const fetchTransactionFeeSettings = createAsyncThunk(
  "settings/fetchTransactionFeeSettings",
  async (_, { rejectWithValue }) => {
    try {
      const res = await getTransactionFeeSettings();
      return res.data;
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ??
          "Failed to fetch transaction fee settings"
      );
      return rejectWithValue(
        error.response?.data?.message ??
          "Failed to fetch transaction fee settings"
      );
    }
  }
);

export const updateTransactionFeeSettingsThunk = createAsyncThunk(
  "settings/updateTransactionFeeSettings",
  async (data: any, { rejectWithValue }) => {
    try {
      const res = await updateTransactionFeeSettings(data);
      return res.data;
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ??
          "Failed to update transaction fee settings"
      );
      return rejectWithValue(
        error.response?.data?.message ??
          "Failed to update transaction fee settings"
      );
    }
  }
);

// Slice
const settingsSlice = createSlice({
  name: "settings",
  initialState,
  reducers: {
    resetSettingsState: (state) => {
      state.general = null;
      state.courierFee = null;
      state.kyc = null;
      state.transactionFee = null;
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    const allThunks = [
      fetchGeneralSettings,
      updateGeneralSettingsThunk,
      fetchCourierFeeSettings,
      updateCourierFeeSettingsThunk,
      fetchKycSettings,
      updateKycSettingsThunk,
      fetchTransactionFeeSettings,
      updateTransactionFeeSettingsThunk,
    ];

    allThunks.forEach((thunk) => {
      builder.addCase(thunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      });
      builder.addCase(thunk.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        switch (thunk.typePrefix.split("/")[1]) {
          case "fetchGeneralSettings":
          case "updateGeneralSettings":
            state.general = action.payload;
            break;
          case "fetchCourierFeeSettings":
          case "updateCourierFeeSettings":
            state.courierFee = action.payload;
            break;
          case "fetchKycSettings":
          // case "updateKycSettings":
            state.kyc = action.payload;
            break;
          case "fetchTransactionFeeSettings":
          case "updateTransactionFeeSettings":
            state.transactionFee = action.payload;
            break;
        }
      });
      builder.addCase(thunk.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      });
    });
  },
});

export const { resetSettingsState } = settingsSlice.actions;

export default settingsSlice.reducer;
